/**
 * The L2 entity-detail shell (dashboard design rules §1 / §4.1): a right-hand
 * `Drawer` with `Breadcrumbs` back up the drill path, a `DescriptionList` of
 * the entity's raw values, and a `Timeline` slot for its event history.
 *
 * Keep the open/close state in the URL (pair with `useDrawerParam` from
 * `lib/drill`) so the drawer is shareable and the back button closes it.
 */

import {
  type BreadcrumbItem,
  Breadcrumbs,
  DescriptionList,
  type DescriptionListEntry,
  Drawer,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

export interface DetailDrawerProps {
  /** Controlled open state — usually `drawer.value === entity.id`. */
  open: boolean;
  /** Close callback — usually `drawer.close` from `useDrawerParam`. */
  onClose: () => void;
  /** Entity heading, e.g. the alert name or site name. */
  title: ReactNode;
  /**
   * Drill path back up, root first; the entity itself is appended as the
   * current crumb automatically. Use `to` for router-navigated crumbs.
   */
  breadcrumbs?: { label: ReactNode; to?: string }[];
  /** The entity's raw values, rendered as a `DescriptionList`. */
  items?: DescriptionListEntry[];
  /** Event history — pass a `Timeline` (rendered below the description list). */
  timeline?: ReactNode;
  /** Extra body content below the slots (tables, alerts, actions). */
  children?: ReactNode;
  /** Pinned footer content (e.g. a "View in Azure portal" link). */
  footer?: ReactNode;
}

export function DetailDrawer({
  open,
  onClose,
  title,
  breadcrumbs,
  items,
  timeline,
  children,
  footer,
}: DetailDrawerProps) {
  const crumbs: BreadcrumbItem[] = [
    ...(breadcrumbs ?? []).map((crumb) => ({
      label: crumb.label,
      render: crumb.to
        ? ({ className, children: crumbChildren }: { className: string; children: ReactNode }) => (
            <Link to={crumb.to as string} className={className}>
              {crumbChildren}
            </Link>
          )
        : undefined,
    })),
    { label: title, current: true },
  ];

  return (
    <Drawer.Root
      side="right"
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Viewport>
          <Drawer.Popup>
            <Drawer.Header>
              <div className="flex flex-col gap-1">
                {crumbs.length > 1 && <Breadcrumbs items={crumbs} />}
                <Drawer.Title>{title}</Drawer.Title>
              </div>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body>
              <div className="flex flex-col gap-5">
                {items && items.length > 0 && <DescriptionList items={items} />}
                {timeline}
                {children}
              </div>
            </Drawer.Body>
            {footer && <Drawer.Footer>{footer}</Drawer.Footer>}
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
